import React from 'react';
import { MonthPoint, RiskFlag, SeriesPoint, SeriesField } from '../types/superadmin';
import { clampPct, codeOf, fmtInt, monthLabel, tone } from '../lib/format';
import { flagTone, statusTone } from '../lib/flags';

/* ---------- tiny inline bar chart ---------- */
export function Spark({ data, tone: t }: { data: number[]; tone?: string }) {
  const max = Math.max(1, ...data);
  return (
    <div className={'spark ' + (t || '')}>
      {data.map((v, i) => (
        <span
          key={i}
          className={i === data.length - 1 ? 'now' : ''}
          style={{ height: Math.max(4, (v / max) * 36) + 'px' }}
        />
      ))}
    </div>
  );
}

/**
 * EVERY bar prints its value — a chart that needs hovering to be read is not
 * doing its job. Dense charts keep the labels by shrinking the type and closing
 * the gap between bars rather than by dropping labels; the hover tooltip stays
 * as the way to read an exact value with its date.
 *
 * Widths at the narrowest place these render (a half-width chart in the 2-up
 * `.split` / `.chart-grid`, ~578px of plot box): 30 bars at `den-3` gives a
 * ~16px column against a ~14px 3-digit label at 8px mono.
 */
const DENSE_BAR_COUNT = 15;
const VERY_DENSE_BAR_COUNT = 25;

/** Extra class that trades type size + gutter for room to label every bar. */
function densityClass(barCount: number): string {
  if (barCount >= VERY_DENSE_BAR_COUNT) return ' den-3';
  if (barCount >= DENSE_BAR_COUNT) return ' den-2';
  return '';
}

/* ---------- large expandable by-month bar graph ---------- */
export function MonthlyBars({ data, unit }: { data: MonthPoint[]; unit?: string }) {
  const [hover, setHover] = React.useState<number | null>(null);
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className={'bargraph' + densityClass(data.length)}>
      {data.map((d, i) => (
        <div
          key={d.month}
          className={'bar-col' + (i === data.length - 1 ? ' now' : '') + (hover === i ? ' hover' : '')}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
        >
          {hover === i && (
            <div className="bar-tip">
              <b>{fmtInt(d.value)}{unit || ''}</b>
              <span>{monthLabel(d.month)}</span>
            </div>
          )}
          <span className="bar-val">{fmtInt(d.value)}</span>
          {/* 120, not BAR_MAX_HEIGHT: this chart carries a label BOTH above
              (value) and below (month), so it has one more row to fit than
              SeriesBars does inside the same 180px plot box. */}
          <div className="bar" style={{ height: Math.max(3, (d.value / max) * 120) + 'px' }} />
          <span className="bar-lbl">{monthLabel(d.month)}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------- by-day series bar chart (for a date range) ---------- */
const BAR_MAX_HEIGHT = 130;

/**
 * By-day bar chart for a date range. Bars sit in a bordered plot box; the date
 * labels live in their own axis row BELOW the box, so every bar is equal width
 * and the labels never crowd the plot. Supports both count fields (minutes,
 * sessions) and 0-100 score fields (pass `maxValue={100}`) — score days with no
 * data are `null` and render as an empty slot (no bar).
 */
export function SeriesBars({
  data,
  field,
  unit,
  maxValue
}: {
  data: SeriesPoint[];
  field: SeriesField;
  unit?: string;
  maxValue?: number;
}) {
  const [hover, setHover] = React.useState<number | null>(null);
  if (!data || data.length === 0) {
    return <div className="empty-state">No activity in this range.</div>;
  }
  const numericVals = data.map((d) => d[field]).filter((v): v is number => typeof v === 'number');
  const max = maxValue ?? Math.max(1, ...numericVals);
  const step = Math.ceil(data.length / 8);
  // The density class rides on `.chart` too: the axis row mirrors the bargraph's
  // gap by hand, so shrinking one without the other unpins every date label
  // from its bar.
  const density = densityClass(data.length);
  return (
    <div className={'chart' + density}>
      <div className={'bargraph' + density}>
        {data.map((d, i) => {
          const value = d[field] as number | null;
          const height = value == null ? 0 : Math.max(3, (value / max) * BAR_MAX_HEIGHT);
          return (
            <div
              key={d.date}
              className={'bar-col' + (i === data.length - 1 ? ' now' : '') + (hover === i ? ' hover' : '')}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              {hover === i && (
                <div className="bar-tip">
                  <b>{value == null ? '—' : fmtInt(value) + (unit || '')}</b>
                  <span>{d.date}</span>
                </div>
              )}
              {/* Printed value, so the chart is readable without hovering.
                  A null day prints nothing — an empty slot, not a zero. */}
              {value != null && <span className="bar-val">{fmtInt(value)}</span>}
              <div className="bar" style={{ height: height + 'px' }} />
            </div>
          );
        })}
      </div>
      <div className="bar-axis">
        {data.map((d, i) => (
          <span key={d.date} className="axis-lbl">
            {i % step === 0 ? d.date.slice(5) : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------- scorecard ---------- */
interface ScoreCardProps {
  label: string;
  value: React.ReactNode;
  prefix?: string;
  unit?: string;
  delta?: number | null;
  deltaTone?: string;
  sparkData?: number[];
  sparkTone?: string;
  sub?: string;
  feature?: boolean;
  onExpand?: () => void;
}

export function ScoreCard(props: ScoreCardProps) {
  const { label, value, prefix, unit, delta, deltaTone, sparkData, sparkTone, sub, feature, onExpand } = props;
  return (
    <div className={'card' + (feature ? ' feature' : '')}>
      <div>
        <div className="lbl">{label}</div>
        <div className="val">
          {prefix && <span className="pre">{prefix}</span>}
          <span>{value}</span>
          {unit && <span className="unit">{unit}</span>}
          {delta != null && (
            <span className={'delta ' + (deltaTone || (delta >= 0 ? 'pos' : 'neg'))}>
              {delta >= 0 ? '↑' : '↓'} {Math.abs(delta)}%
            </span>
          )}
        </div>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {sparkData && (
        <div>
          <Spark data={sparkData} tone={sparkTone} />
          <div className="footer-meta">
            <span>Last 12 mo</span>
            {onExpand && (
              <span
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={onExpand}
                onKeyDown={(e) => e.key === 'Enter' && onExpand()}
              >
                Expand ↗
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- client name cell ---------- */
export function ClientCell({ name, code, sub }: { name: string; code?: string; sub?: string }) {
  return (
    <div>
      <div className="client">
        <span className="mini">{code || codeOf(name)}</span>
        <span>{name}</span>
      </div>
      {sub && <div className="sub-row">{sub}</div>}
    </div>
  );
}

/* ---------- at-risk flag chips ---------- */
export function FlagsCell({ flags }: { flags: RiskFlag[] }) {
  if (!flags || flags.length === 0) {
    return <span className="caption">No flags</span>;
  }
  return (
    <div className="flags">
      {flags.map((f) => (
        <span key={f.key} className={'chip ' + flagTone(f.key)} title={f.detail}>
          {f.detail || f.label}
        </span>
      ))}
    </div>
  );
}

/* ---------- rep name cell ---------- */
export function RepCell({
  initials,
  name,
  role,
  risk
}: {
  initials: string;
  name: string;
  role?: string;
  risk?: boolean;
}) {
  return (
    <div>
      <div className="rep">
        <span className="av" style={risk ? { background: 'var(--neg)' } : undefined}>
          {initials}
        </span>
        <span>{name}</span>
      </div>
      {role && <div className="rep-sub">{role}</div>}
    </div>
  );
}

/* ---------- 0-100 metric with bar ---------- */
export function MetricBar({ value }: { value: number | null }) {
  if (value == null) return <span className="caption">—</span>;
  const v = clampPct(value);
  const t = tone(v);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
      <span className={'metric-num ' + t}>
        {v}
        <span className="u">%</span>
      </span>
      <span className={'row-bar ' + t}>
        <span style={{ width: v + '%' }} />
      </span>
    </span>
  );
}

/* ---------- compact 0-100 score (no bar) for dense columns ---------- */
export function ScoreCell({ value }: { value: number | null }) {
  if (value == null) return <span className="caption">—</span>;
  const v = clampPct(value);
  return (
    <span className={'metric-num ' + tone(v)}>
      {v}
      <span className="u">%</span>
    </span>
  );
}

/* ---------- status pill ---------- */
export function StatusPill({ status }: { status: string }) {
  return (
    <span className={'status-pill ' + statusTone(status)}>
      <span className="dot" />
      {status}
    </span>
  );
}

/* ---------- generic flag/filter chips ---------- */
interface FilterChipsProps {
  label?: string;
  options: Array<{ id: string; label: string }>;
  counts: Record<string, number>;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onClear: () => void;
}

export function FilterChips({ label, options, counts, selected, onToggle, onClear }: FilterChipsProps) {
  return (
    <div className="filter-chips">
      {label && <span className="label">{label}</span>}
      {options.map((f) => {
        const n = counts[f.id] ?? 0;
        const on = selected.has(f.id);
        return (
          <button
            key={f.id}
            className={'filter-chip' + (on ? ' on' : '') + (n === 0 && !on ? ' disabled' : '')}
            onClick={() => onToggle(f.id)}
            disabled={n === 0 && !on}
          >
            {f.label}
            <span className="c">{n}</span>
          </button>
        );
      })}
      {selected.size > 0 && (
        <button className="filter-clear" onClick={onClear}>
          Clear ×
        </button>
      )}
    </div>
  );
}

/* ---------- states ---------- */
export const LoadingState = ({ label = 'Loading…' }: { label?: string }) => (
  <div className="loading-state">{label}</div>
);

export const ErrorState = ({ message }: { message: string }) => (
  <div className="error-state">{message}</div>
);

export const EmptyState = ({ children }: { children: React.ReactNode }) => (
  <div className="empty-state">{children}</div>
);

/* ---------- numeric helper for tables ---------- */
export const Num = ({ value, unit }: { value: number; unit?: string }) => (
  <span className="metric-num">
    {fmtInt(value)}
    {unit && <span className="u">{unit}</span>}
  </span>
);
